import React from "react";
import { Icon, EmptyState, notify } from "./registry";
import { ORDER_STATUS, cents } from "./lib/printkit";
import type { PrintManifest } from "./lib/printkit";
import { orderTimeline, reorderInto, receiptHtml } from "./lib/printorders";
import type { ApparelCatalog } from "./lib/printorders";
import { ApparelLineSummary } from "./printapparel";
/* ============================================================
   Fence Works — Print Kit · Orders
   What the contractor ordered and where it is: Placed → Paid →
   Sent to printer → Shipped (carrier + tracking) → Delivered, with
   the receipt, the print files and Reorder (the lines go back into
   the cart, so the readiness checks run again before paying).
   ============================================================ */

const shortDate = (v: any) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export function OrdersView({ live, manifest, catalog, kit, saveKit, brandLogo, highlight, openCart }: {
  live: boolean; manifest: PrintManifest; catalog: ApparelCatalog | null; kit: any; saveKit: (p: any) => void; brandLogo: string; highlight?: string; openCart: () => void;
}) {
  const DB: any = window.VFP_DB;
  const [orders, setOrders] = React.useState<any>(null);
  const [paying, setPaying] = React.useState<any>("");
  const load = React.useCallback(() => { if (!live) { setOrders([]); return; } DB.print.orders().then(setOrders).catch((e: any) => { notify(String((e && e.message) || e)); setOrders([]); }); }, [live]);
  React.useEffect(() => { load(); }, [load]);
  // back from checkout: the webhook may still be landing — look again a few times
  React.useEffect(() => { if (!highlight || !live) return; let n = 0; const t = setInterval(() => { load(); if (++n >= 6) clearInterval(t); }, 2500); return () => clearInterval(t); }, [highlight, live, load]);

  async function pay(o: any) {
    setPaying(o.id);
    try { const r = await DB.print.pay(o.id); if (r && r.url) window.location.href = r.url; else notify("No payment link yet — try again in a moment."); }
    catch (e: any) { notify(String((e && e.message) || e)); }
    finally { setPaying(""); }
  }
  function reorder(o: any) {
    const r = reorderInto(o, { templates: manifest.templates, catalog, kit });
    if (!r.copied.length) { notify("Nothing from " + o.id + " can be reordered — those pieces aren't offered any more."); return; }
    saveKit({ cart: r.cart, apparel: r.apparel, reorderOf: o.id });
    notify(r.copied.length + " line" + (r.copied.length > 1 ? "s" : "") + " from " + o.id + " copied into your order — check them and place it." + (r.skipped.length ? " Not offered any more: " + r.skipped.join(", ") + "." : ""));
    openCart();
  }
  function receipt(o: any) {
    const html = receiptHtml(o, { money: cents });
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  if (!live) return <EmptyState icon="box" title="Orders live on the online service" sub="Placed orders, their progress and tracking show here when you're signed in at app.fenceworks.ai." />;
  if (orders == null) return <p className="muted">Loading your orders…</p>;
  if (!orders.length) return <EmptyState icon="box" title="No print orders yet" sub="Add pieces or apparel to the order and place it — you'll follow it here from payment to your door." />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {orders.map((o: any) => {
        const st = ORDER_STATUS[o.status] || { label: o.status, chip: "", hint: "" };
        const steps = orderTimeline(o);
        const paid = !!o.paidAt;
        return (
          <div key={o.id} className="card pad pk-order" style={{ padding: 14, borderColor: highlight === o.id ? "var(--teal)" : undefined }}>
            <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{o.id} <span className={"chip " + st.chip} style={{ fontSize: 10.5, marginLeft: 4 }}>{st.label}</span></div>
                <div className="faint" style={{ fontSize: 11.5 }}>Placed {o.createdAt ? new Date(o.createdAt).toLocaleDateString() : ""}{o.reorderOf ? " · reorder of " + o.reorderOf : ""}</div>
              </div>
              <div style={{ fontWeight: 900, fontSize: 17 }}>{o.totalCents != null ? cents(o.totalCents) : <span className="muted" style={{ fontSize: 13, fontWeight: 600 }}>being priced</span>}</div>
            </div>

            <ol className="pk-timeline" aria-label={"Progress of order " + o.id}>
              {steps.map((s) => (
                <li key={s.key} className={"pk-step " + s.state + (s.key === "cancelled" || s.key === "refunded" ? " ended" : "")}>
                  <span className="pk-dot">{s.state === "done" ? <Icon name={s.key === "cancelled" || s.key === "refunded" ? "x" : "check"} size={11} /> : null}</span>
                  <span className="pk-step-t">
                    <b>{s.label}</b>
                    {s.at && <span className="faint">{shortDate(s.at)}</span>}
                    {s.detail && (s.link ? <a href={s.link} target="_blank" rel="noopener" style={{ color: "var(--teal)" }}>{s.detail} <Icon name="arrow" size={10} /></a> : <span className="faint">{s.detail}</span>)}
                  </span>
                </li>
              ))}
            </ol>

            <div style={{ display: "grid", gap: 6, marginTop: 4 }}>
              {(o.items || []).map((it: any, i: number) => it.kind === "apparel" || String(it.templateId || "").startsWith("apparel:")
                ? <ApparelLineSummary key={i} catalog={catalog} line={it} logo={brandLogo} right={<span className="tnum" style={{ fontSize: 12.5 }}>{cents(it.priceCents)}</span>} />
                : (
                  <div key={i} className="row gap-10" style={{ alignItems: "center" }}>
                    <span style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--line)", display: "grid", placeItems: "center" }}><Icon name="printer" size={18} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 13.5 }}>{it.name} <span className="faint" style={{ fontWeight: 500 }}>× {Number(it.quantity).toLocaleString()}</span></div><div className="faint" style={{ fontSize: 11 }}>{it.product}</div></div>
                    <span className="tnum" style={{ fontSize: 12.5 }}>{cents(it.priceCents)}</span>
                  </div>
                ))}
            </div>
            {st.hint && !paid && <div className="faint" style={{ fontSize: 11.5, marginTop: 8 }}>{st.hint}</div>}

            <div className="row gap-6" style={{ marginTop: 10, flexWrap: "wrap" }}>
              {o.status === "pending_payment" && <button className="btn btn-primary btn-sm" disabled={paying === o.id} onClick={() => pay(o)}><Icon name="dollar" size={13} />{paying === o.id ? "Opening…" : "Pay " + cents(o.totalCents)}</button>}
              {o.trackingUrl && o.status === "shipped" && <a className="btn btn-primary btn-sm" href={o.trackingUrl} target="_blank" rel="noopener"><Icon name="mappin" size={13} />Track package</a>}
              {paid && <button className="btn btn-outline btn-sm" onClick={() => receipt(o)}><Icon name="clipboard" size={13} />Receipt</button>}
              <button className="btn btn-outline btn-sm" onClick={() => reorder(o)}><Icon name="undo" size={13} />Reorder</button>
              {(o.files || []).filter((f: any) => f.kind !== "artwork").map((f: any) => { const it = (o.items || []).find((x: any) => (f.lineId && x.lineId ? x.lineId === f.lineId : x.templateId === f.templateId)); return (
                <a key={f.index} className="btn btn-ghost btn-sm" href={DB.print.fileUrl(o.id, f.index)} target="_blank" rel="noopener"><Icon name="download" size={12} />{(it ? it.name : f.templateId) + (f.kind === "mockup" ? " mockup" : " PDF")}</a>); })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
